import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Plus, Check, Bookmark, ChevronDown, Heart, Info } from 'lucide-react';
import { normalizeStructuredContent } from '../utils/structuredReply';
import SavePicker from './SavePicker';
import ChatProductCard from './ChatProductCard';
import ShadeMatchCard from './ShadeMatchCard';
import IngredientGlossarySheet from './IngredientGlossarySheet';
import BrandReasonsSheet from './BrandReasonsSheet';
import { glossarySegments } from '../utils/ingredientGlossary';

// Shared product card container: distinct background, rounded corners, spacing (image 2 style)
const productCardClass = 'chat-structured-product-card relative rounded-xl border border-[#2A324B]/12 bg-white shadow-sm p-3.5 mb-3 last:mb-0';

const EMPTY_SECTION_COPY = {
  products: 'No separate product list for this reply — see Advice above.',
  routine: 'No separate routine steps for this reply — see Advice above.',
};

const TAB_DEFS = [
  { id: 'advice', label: 'Advice' },
  { id: 'products', label: 'Products' },
  { id: 'routine', label: 'Routine' },
  { id: 'general', label: 'General' },
];

// Normalize a name for fuzzy lookup: lowercase, strip accents, collapse non-alphanumerics.
// We use this on both sides (LLM-emitted text + catalog payload) so paraphrasing
// like "Atelier IV" vs "ATELIER IV" or "Westman Atelier Vital Skin..." vs "WESTMAN ATELIER Vital Skin..."
// still matches.
const _norm = (s) => (s || '')
  .toString()
  .toLowerCase()
  .normalize('NFKD')
  .replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim();

const publicCatalogImageUrl = (value, folder) => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:'
      && url.hostname === 'doatsodmpimjcqtpdawx.supabase.co'
      && url.pathname.startsWith(`/storage/v1/object/public/catalog-images/${folder}/`)
      && !url.search && !url.hash ? url.href : '';
  } catch (_) {
    return '';
  }
};

const clarifyOptionDisplay = (option, question, options) => {
  const commonFirstWord = options[0]?.split(/\s+/)[0] || '';
  const repeatsBrand = commonFirstWord.length > 1
    && options.every((value) => value.toLowerCase().startsWith(`${commonFirstWord.toLowerCase()} `))
    && question.toLowerCase().split(/\W+/).includes(commonFirstWord.toLowerCase());
  const label = repeatsBrand ? option.slice(commonFirstWord.length).trim() : option;
  const parts = label.split(/\s+with\s+/i);
  return { title: parts[0], detail: parts.length > 1 ? parts.slice(1).join(' with ') : '' };
};

// Relative luminance (0=black, 1=white) from a #rrggbb hex — used to order a
// shade trio lightest → darkest so the row reads left (lighter) → right (darker).
const _lum = (hex) => {
  const h = String(hex || '').replace('#', '');
  if (h.length < 6) return 0.5;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return 0.5;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
};

// Depth ordering for the shade trio. Foundation shade NAMES reliably encode depth
// (a number: "0 N", "2 N", "2,5 N", "L3.25"…), so parse that first — far more reliable
// than hex, which is often missing on image-only swatches (that made hex default to 0.5
// so BOTH neighbours labelled "lighter"). Fall back to inverse luminance when no number.
const _depthNum = (name) => {
  const m = String(name || '').match(/(\d+(?:[.,]\d+)?)/);
  if (!m) return null;
  const v = parseFloat(m[1].replace(',', '.'));
  return Number.isFinite(v) ? v : null;
};
const _depthKey = (s) => {
  const d = _depthNum(s && s.shadeName);
  return d !== null ? d : (1 - _lum(s && s.hex)); // higher = darker
};

// Small circular swatch — prefer the catalog image; the hex is a fallback when
// there is no image or the image fails to load.
const ShadeSwatch = ({ imageUrl, hex, size = 28, alt = '' }) => {
  if (!imageUrl && !hex) return null;
  const style = {
    width: size,
    height: size,
    backgroundColor: hex || '#E8DCC8',
  };
  return (
    <span
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      style={style}
      className="inline-block flex-shrink-0 overflow-hidden rounded-full ring-1 ring-[#2A324B]/15 shadow-[0_1px_2px_rgba(42,50,75,0.12),inset_0_0_0_1px_rgba(255,255,255,0.4)] align-middle"
    >
      {imageUrl && <img src={imageUrl} alt="" aria-hidden="true" className="w-full h-full object-cover" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}
    </span>
  );
};

const StructuredMessage = ({ content: rawContent, onSaveProduct, userId, shadeMatches, onAsk, clarifyAnswered = false, noReasoningSplit, sectionCta = null }) => {
  const content = useMemo(() => normalizeStructuredContent(rawContent) || rawContent, [rawContent]);
  const [savedProducts, setSavedProducts] = useState(new Set());
  const [brandSheetOpen, setBrandSheetOpen] = useState(false);
  const brandReasons = Array.isArray(content?.brand_reasons)
    ? content.brand_reasons.filter((item) => item && item.brand && item.product_id && item.reason)
    : [];
  const brandTrigger = brandReasons.length > 0 ? (
    <button type="button" onClick={() => setBrandSheetOpen(true)}
      className="inline-flex items-center gap-1.5 py-1 text-[12px] font-semibold text-[#8B6852]"
      aria-label="Why these brands?">
      <Info className="h-4 w-4" /> Why these brands?
    </button>
  ) : null;
  const brandSheet = brandSheetOpen && brandReasons.length > 0 ? (
    <BrandReasonsSheet reasons={brandReasons} onClose={() => setBrandSheetOpen(false)} />
  ) : null;
  // Save flow (#26): the heart/save buttons open the "Save to…" picker (pendingSave);
  // confirmSave commits with the chosen list + usage tags. (Sofia 2026-06-30)
  const [pendingSave, setPendingSave] = useState(null); // { data, productKey }
  const [glossaryEntry, setGlossaryEntry] = useState(null);
  const [clarifySubmitted, setClarifySubmitted] = useState(false);
  const [selectedClarifyOption, setSelectedClarifyOption] = useState(null);
  const clarifyTimer = useRef(null);
  useEffect(() => () => { if (clarifyTimer.current) clearTimeout(clarifyTimer.current); }, []);
  const confirmSave = async ({ list_category, usage_tags, recommended_shade, swatch_hex, swatch_image_url }) => {
    if (!pendingSave) return;
    const _key = pendingSave.productKey;
    // Await the real save; only flip the heart to "saved" if the backend actually
    // persisted it, so a failed save can't show a false "Saved" state.
    const selectedShade = recommended_shade
      ? { recommended_shade, swatch_hex: swatch_hex || null, swatch_image_url: swatch_image_url || null }
      : {};
    const _saved = await onSaveProduct({ ...pendingSave.data, ...selectedShade, usage_tags }, list_category);
    if (_saved) setSavedProducts((prev) => new Set([...prev, _key]));
    setPendingSave(null);
  };

  // Build a lookup index from the backend's shadeMatches payload.
  // Key by (normalized product_title, normalized shade_name) for precise lookup,
  // and also keep a shade-only fallback so an LLM block that paraphrases the
  // product title still resolves the swatch.
  //
  // BULLETPROOF T1: catalog often stores verbose shade names like
  // "3W1 Dusk - light medium with warm undertones" but the LLM renders just
  // "3W1 Dusk" in cards. Index BOTH the full form AND the head (text before any
  // em/en-dash) so short-form lookups succeed.
  const swatchIndex = useMemo(() => {
    const byProductShade = new Map();
    const byShade = new Map();
    // Bulletproof V: separate by-product index so skincare products (no shade)
    // are still lookup-able for their clean hero image.
    const byProduct = new Map();
    // Per-product ORDERED shade list (makeup) — powers the lighter/best/darker swatch trio.
    const byProductList = new Map();
    if (Array.isArray(shadeMatches)) {
      shadeMatches.forEach((m) => {
        if (!m) return;
        const pt = _norm(m.product_title);
        const fullShadeName = m.shade_name || '';
        const sn = _norm(fullShadeName);
        const entry = {
          // The catalog's public swatch image is more faithful than its derived
          // hex (which can describe product packaging instead of makeup).
          imageUrl: publicCatalogImageUrl(m.swatch_image_url, 'swatches')
            || publicCatalogImageUrl(m.swatch_image_url, 'heroes'),
          hex: /^#[0-9a-f]{6}$/i.test(m.hex_color || '') ? m.hex_color : '',
          productTitle: m.product_title || '',
          shadeName: m.shade_name || '',
          productHeroImageUrl: m.product_hero_image_url || '',
          productHeroBadge: m.product_hero_badge || '',
        };
        // Always index by product (used for skincare hero lookup, and as a
        // last-resort fallback for makeup when shade name doesn't match).
        if (pt && !byProduct.has(pt)) byProduct.set(pt, entry);
        // Shade-indexed lookups only when we have a shade name (makeup).
        if (sn) {
          if (pt) byProductShade.set(`${pt}||${sn}`, entry);
          if (!byShade.has(sn)) byShade.set(sn, entry);
          if (pt) {
            if (!byProductList.has(pt)) byProductList.set(pt, []);
            if (!byProductList.get(pt).some((e) => _norm(e.shadeName) === sn)) {
              byProductList.get(pt).push(entry);
            }
          }
          // BULLETPROOF T1: also index by the head (before " — " / " – " / " - ").
          // Catalog: "3W1 Dusk - light medium..." → head "3W1 Dusk".
          const headRaw = fullShadeName.split(/\s+[—–-]\s+/)[0];
          if (headRaw && headRaw.trim() && headRaw.trim() !== fullShadeName.trim()) {
            const headKey = _norm(headRaw);
            if (headKey && headKey !== sn) {
              if (pt && !byProductShade.has(`${pt}||${headKey}`)) {
                byProductShade.set(`${pt}||${headKey}`, entry);
              }
              if (!byShade.has(headKey)) byShade.set(headKey, entry);
            }
          }
        }
      });
    }
    const lookup = (productTitle, shadeName) => {
      const sn = _norm(shadeName);
      if (!sn) return null;
      const pt = _norm(productTitle);
      if (pt) {
        const exact = byProductShade.get(`${pt}||${sn}`);
        if (exact) return exact;
        // Try product-title substring match (LLM may add/drop words like "Dewy Skin Tint").
        for (const [key, val] of byProductShade) {
          const [keyPt, keySn] = key.split('||');
          if (keySn !== sn) continue;
          if (keyPt.includes(pt) || pt.includes(keyPt)) return val;
        }
      }
      return byShade.get(sn) || null;
    };
    // Bulletproof V: product-only lookup for skincare cards (no shade).
    // Used to retrieve the clean hero image when Recommended shade is N/A.
    const lookupProduct = (productTitle) => {
      const pt = _norm(productTitle);
      if (!pt) return null;
      const exact = byProduct.get(pt);
      if (exact) return exact;
      // Substring match — LLM may add/drop words from product title.
      for (const [keyPt, val] of byProduct) {
        if (keyPt.includes(pt) || pt.includes(keyPt)) return val;
      }
      return null;
    };
    // All swatch-bearing shades for a product, in payload order (best, neighbours…).
    const lookupProductShades = (productTitle) => {
      const pt = _norm(productTitle);
      if (!pt) return [];
      if (byProductList.has(pt)) return byProductList.get(pt);
      for (const [keyPt, val] of byProductList) {
        if (keyPt.includes(pt) || pt.includes(keyPt)) return val;
      }
      return [];
    };
    return { lookup, lookupProduct, lookupProductShades, size: byProductShade.size + byShade.size + byProduct.size };
  }, [shadeMatches]);
  // Format inline text: handles **bold** and *bold* (single asterisk)
  const formatInlineText = (text) => {
    if (typeof text !== 'string') return String(text || '');
    // Strip Unicode checkbox/box characters the AI sometimes emits as decorative separators
    // e.g. ☐ ☑ ☒ ⊠ ✕ ✗ ✘ □
    text = text.replace(/[\u2610\u2611\u2612\u22A0\u2714\u2715\u2717\u2718\u25A1]/g, '').replace(/  +/g, ' ').trim();

    // Split on **bold** or *bold* patterns, keeping delimiters
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g);

    const ingredientParts = (value, prefix) => glossarySegments(value).map((segment, index) =>
      segment.id ? (
        <button key={`${prefix}-${index}`} type="button"
          className="inline underline decoration-dotted decoration-[#5574AA] underline-offset-4"
          onClick={() => setGlossaryEntry(segment.id)}
          aria-label={`About ${segment.text}`}>{segment.text}</button>
      ) : <React.Fragment key={`${prefix}-${index}`}>{segment.text}</React.Fragment>
    );
    const result = [];
    parts.forEach((part, index) => {
      if (part.match(/^\*\*[^*]+\*\*$/)) {
        // **double asterisk** → bold
        const boldText = part.replace(/^\*\*|\*\*$/g, '');
        result.push(<span key={index} className="font-semibold text-[#2A324B]">{ingredientParts(boldText, index)}</span>);
      } else if (part.match(/^\*[^*]+\*$/)) {
        // *single asterisk* → bold (AI sometimes uses this style)
        const boldText = part.replace(/^\*|\*$/g, '');
        result.push(<span key={index} className="font-semibold text-[#2A324B]">{ingredientParts(boldText, index)}</span>);
      } else if (part) {
        result.push(...ingredientParts(part, index));
      }
    });

    return result.length === 1 ? result[0] : result;
  };

  // Advice / general text formatting — line-by-line with proper bullets and bold
  const formatText = (text, comfortable = false) => {
    if (!text) return null;

    // Arrays: join to string
    if (Array.isArray(text)) text = text.join('\n');
    if (typeof text !== 'string') text = String(text);

    const lines = text.split('\n');
    const nodes = [];
    let key = 0;
    // Track whether we're inside a numbered item so bullets that belong to it
    // render indented (sub-point hierarchy) rather than flush with the number.
    let inNumberedItem = false;

    lines.forEach((line) => {
      const trimmed = line.trim();

      // Empty line → tight spacer (matches pre-tab compact layout).
      // A blank line doesn't end a numbered item — sub-bullets often follow one.
      if (!trimmed) {
        nodes.push(<div key={key++} className={comfortable ? 'h-0.5' : 'h-1'} />);
        return;
      }

      // Markdown heading: ##, ###, #### etc → styled heading
      const headingMatch = trimmed.match(/^#{1,6}\s+(.+)$/);
      if (headingMatch) {
        inNumberedItem = false;
        nodes.push(
          <p key={key++} className={`text-sm font-semibold text-[#2A324B] mb-0 ${comfortable ? 'mt-3 leading-relaxed' : 'mt-1.5'}`}>
            {formatInlineText(headingMatch[1])}
          </p>
        );
        return;
      }

      // Bullet line: starts with - – • or * (as bullet marker, not bold).
      // When it follows a numbered item, indent it as a sub-point.
      const bulletMatch = trimmed.match(/^[-–•*]\s+(.+)$/);
      if (bulletMatch) {
        nodes.push(
          <div key={key++} className={`flex items-start ${comfortable ? 'gap-2' : 'gap-1.5'} ${inNumberedItem ? 'pl-7' : 'pl-1'}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-[#2A324B]/70 flex-shrink-0 mt-[5px]" />
            <p className={`text-sm text-[#2A324B]/90 flex-1 min-w-0 ${comfortable ? 'leading-relaxed' : 'leading-snug'}`}>
              {formatInlineText(bulletMatch[1])}
            </p>
          </div>
        );
        return;
      }

      // Numbered list: "1) " or "1. " → inline numbered item
      const numberedMatch = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
      if (numberedMatch) {
        inNumberedItem = true;
        nodes.push(
          <div key={key++} className="flex items-start gap-2 mt-1">
            <span className="text-sm font-semibold text-[#2A324B] flex-shrink-0 w-5 text-right leading-snug">
              {numberedMatch[1]}.
            </span>
            <p className={`text-sm text-[#2A324B] flex-1 min-w-0 font-medium ${comfortable ? 'leading-relaxed' : 'leading-snug'}`}>
              {formatInlineText(numberedMatch[2])}
            </p>
          </div>
        );
        return;
      }

      // Section heading: short line ending with colon or wrapped in **
      const isHeading =
        (trimmed.endsWith(':') && trimmed.length < 80) ||
        /^\*\*[^*]+\*\*[:\s]*$/.test(trimmed);
      if (isHeading) {
        inNumberedItem = false;
        nodes.push(
          <p key={key++} className={`text-sm font-semibold text-[#2A324B] mb-0 ${comfortable ? 'mt-3 leading-relaxed' : 'mt-1.5'}`}>
            {formatInlineText(trimmed.replace(/:$/, ''))}
          </p>
        );
        return;
      }

      // Regular paragraph — ends the numbered context
      inNumberedItem = false;
      nodes.push(
        <p key={key++} className={`text-sm text-[#2A324B]/90 ${comfortable ? 'leading-relaxed' : 'leading-snug'}`}>
          {formatInlineText(trimmed)}
        </p>
      );
    });

    return <div className={comfortable ? 'space-y-2' : 'space-y-0.5'}>{nodes}</div>;
  };

  // Special formatting for Products tab content
  const formatProductContent = (text) => {
    if (!text) return null;

    // #55: normalize structured product payloads (an array of product objects, or
    // a single object) into the canonical "**Name** by Brand / Recommended shade: /
    // Why it works:" block string, so ALL product content flows through the
    // hero-image card renderer below instead of the old text-only object path.
    // (The tool-calling chat path sends objects, which is why cards lost their
    // images in both dev and prod.)
    const _toBlock = (item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        const name = item.name || item.product || item.productName || item.product_name || item.title || item.product_title || '';
        const brand = item.brand || item.productBrand || item.product_brand || item.manufacturer || '';
        const shade = item.recommended_shade || item.recommendedShade || item.shade || item.shade_match || '';
        const desc = item.description || item.reasoning || item.match_reasoning || item.why_recommended || item.explanation || item.summary || '';
        if (name || brand) {
          let block = (name && brand) ? `**${name}** by ${brand}` : `**${name || brand}**`;
          if (shade && String(shade).toUpperCase() !== 'N/A') block += `\nRecommended shade: ${shade}`;
          if (desc) block += `\nWhy it works: ${desc}`;
          return block;
        }
        const vals = Object.values(item).filter((v) => typeof v === 'string' && v.trim());
        return vals.join(' — ');
      }
      return String(item == null ? '' : item).trim();
    };
    if (Array.isArray(text) && text.some((it) => it && typeof it === 'object')) {
      text = text.map(_toBlock).filter(Boolean).join('\n\n');
    } else if (text && typeof text === 'object' && !Array.isArray(text)) {
      text = _toBlock(text);
    }

    // #55 normalised product objects into text blocks above, so the only array that can
    // still reach here is an array of plain strings — join it and let the block/card
    // renderer below handle it. (The old object-card render paths that lived here were
    // unreachable after that normalisation and rendered an image-less legacy card on the
    // rare occasions they did fire, so they've been removed.)
    if (Array.isArray(text)) {
      text = text.join('\n');
    }

    // Ensure text is a string
    if (typeof text !== 'string') {
      console.warn('⚠️ Products content is not string or object:', typeof text, text);
      text = String(text);
    }

    // Product cards: split into blocks (by double newline or by line starting with **)
    let blocks = text.split(/\n\n+/).map(b => b.trim()).filter(Boolean);
    if (blocks.length === 1 && blocks[0].includes('\n') && (blocks[0].match(/\*\*[^*]+\*\*.*\s+by\s+/g) || []).length > 1) {
      blocks = blocks[0].split(/\n(?=\*\*[^*]+\*\*)/).map(b => b.trim()).filter(Boolean);
    }
    // Use card layout for any multi-line or multi-block content (always show separation).
    // #55: also treat a single product-looking block (bold title or "… by Brand") as a
    // card, so a name-only rec still renders the hero-image card (product photo from the
    // swatch index) instead of a plain text line with no image.
    const _looksLikeProduct = blocks[0] && (/\*\*/.test(blocks[0]) || /\s+by\s+/i.test(blocks[0]));
    const useCardLayout = blocks.length > 0 && (blocks.length > 1 || (blocks[0] && blocks[0].includes('\n')) || _looksLikeProduct);

    if (useCardLayout) {
      const detailLabelRegex = /^(Recommended shade|Alternative shade|Why it works|Key ingredients|Texture\/finish|How to apply|How to use|Price range|When to use|Best for|Application|Notes?):\s*(.*)$/i;
      // Helper: build the shade record (display value + swatch lookup) for one shade line.
      // Used to lift the recommended-shade hero element out of the body flow so it can
      // be rendered as the visual anchor at the top of each card.
      const buildShadeRecord = (rawValue, productNameForLookup) => {
        let shadeValue = (rawValue || '').trim();
        if (!shadeValue) return null;
        let swatch = null;
        if (shadeValue !== 'N/A') {
          swatch = swatchIndex.lookup(productNameForLookup, shadeValue);
          if (!swatch) {
            const head = shadeValue.split(/\s+[—–-]\s+/)[0]?.trim();
            if (head && head !== shadeValue) {
              swatch = swatchIndex.lookup(productNameForLookup, head);
            }
          }
        }
        if (shadeValue !== 'N/A' && !swatch) {
          const commaCount = (shadeValue.match(/,/g) || []).length;
          if (commaCount >= 3 && shadeValue.length > 60) {
            shadeValue = 'N/A';
          }
        }
        const displayValue = swatch?.shadeName || shadeValue;
        // Split "Atelier VII - medium honey, neutral undertone" into head + tail
        const dashSplit = displayValue.split(/\s+[—–-]\s+/);
        const shadeHead = dashSplit[0]?.trim() || displayValue;
        const shadeTail = dashSplit.length > 1 ? dashSplit.slice(1).join(' — ').trim() : '';
        return { displayValue, shadeHead, shadeTail, swatch };
      };

      // --- Product-grid detection: an all-products (no shade) message renders as a
      // 2-col grid of vertical cards (photo on top). Shade matches stay full-width.
      const _isShadeBlock = (block) => {
        const ls = block.split('\n').map((l) => l.trim()).filter(Boolean);
        const st = (ls[0] || '').replace(/\*\*/g, '');
        const bm = st.match(/^(.+?)\s+by\s+(.+)$/);
        const pName = bm ? bm[1].trim() : st;
        const hasShadeLine = ls.slice(1).some((line) => {
          const match = line.match(/^(Recommended|Alternative) shade:\s*(.*)$/i);
          return match && match[2].trim() && match[2].trim().toUpperCase() !== 'N/A';
        });
        return hasShadeLine || (swatchIndex.lookupProductShades(pName) || []).length > 0;
      };
      // >= 1, not >= 2: a SINGLE product rec must render as the same square-photo card,
      // not fall through to the text-led layout below. That count-dependent fork was why
      // the card design appeared to change between replies (and looked like a bad deploy).
      const _useGrid = blocks.length >= 1 && blocks.every((b) => !_isShadeBlock(b));

      if (_useGrid) {
        return (
          <div className="grid grid-cols-2 gap-3" data-products-layout="grid">
            {blocks.map((block, blockIndex) => {
              const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
              const strippedTitle = (lines[0] || '').replace(/\*\*/g, '');
              const byMatch = strippedTitle.match(/^(.+?)\s+by\s+(.+)$/);
              const productName = byMatch ? byMatch[1].trim() : strippedTitle;
              const brand = byMatch ? byMatch[2].trim() : '';
              const productKey = `${productName}-${brand}`;
              const isSaved = savedProducts.has(productKey);
              const heroUrl = swatchIndex.lookupProduct(productName)?.productHeroImageUrl;
              return (
                <ChatProductCard
                  key={blockIndex}
                  productName={productName}
                  brand={brand}
                  heroUrl={heroUrl}
                  isSaved={isSaved}
                  canSave={!!(onSaveProduct && userId)}
                  onSave={() => {
                    setPendingSave({ data: { name: productName, brand, category: 'skincare', image: heroUrl || '', description: block, recommended_shade: null }, productKey });
                  }}
                />
              );
            })}
            <div className="col-span-2 mt-1 pt-3 border-t border-[#2A324B]/8 flex items-start gap-1.5 text-[13px] text-[#2A324B]/50 leading-snug">
              <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0 mt-0.5" />
              <span>Chosen for your skin only. We earn no commission from these.</span>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-0" data-products-layout="cards">
          {blocks.map((block, blockIndex) => {
            const lines = block.split('\n').map(l => l.trim()).filter(Boolean);
            const titleLine = lines[0] || '';
            const bodyLines = lines.slice(1);
            const strippedTitle = titleLine.replace(/\*\*/g, '');
            const byMatch = strippedTitle.match(/^(.+?)\s+by\s+(.+)$/);
            const productName = byMatch ? byMatch[1].trim() : strippedTitle;
            const brand = byMatch ? byMatch[2].trim() : '';
            const productKey = `${productName}-${brand}`;
            const isSaved = savedProducts.has(productKey);

            // Pre-extract the Recommended/Alternative shade lines so the swatch can
            // anchor the top of the card as a hero element. Everything else stays
            // in the body flow.
            let primaryShadeRecord = null;
            let alternativeShadeRecord = null;
            const otherBodyLines = [];
            bodyLines.forEach((line) => {
              const m = line.match(detailLabelRegex);
              if (m && /^Recommended shade$/i.test(m[1])) {
                primaryShadeRecord = buildShadeRecord(m[2], productName);
                return;
              }
              if (m && /^Alternative shade$/i.test(m[1])) {
                alternativeShadeRecord = buildShadeRecord(m[2], productName);
                return;
              }
              otherBodyLines.push(line);
            });

            const _hasShade = !!(primaryShadeRecord && primaryShadeRecord.displayValue !== 'N/A');
            const _shadeList = _hasShade ? (swatchIndex.lookupProductShades(productName) || []) : [];
            const showTrio = _shadeList.length >= 2;
            const heroUrl = swatchIndex.lookupProduct(productName)?.productHeroImageUrl;
            let shades = [];
            if (_hasShade && showTrio) {
              const sorted = [..._shadeList].sort((a, b) => _depthKey(a) - _depthKey(b));
              const bestNorm = _norm(primaryShadeRecord.shadeHead || primaryShadeRecord.displayValue);
              const matchBest = (shade) => {
                const name = _norm(shade.shadeName);
                return name === bestNorm || (!!bestNorm && (name.startsWith(bestNorm) || bestNorm.startsWith(name)));
              };
              let visible = sorted;
              if (sorted.length > 3) {
                let bestIndex = sorted.findIndex(matchBest);
                if (bestIndex < 0) bestIndex = Math.floor(sorted.length / 2);
                const start = Math.min(Math.max(0, bestIndex - 1), sorted.length - 3);
                visible = sorted.slice(start, start + 3);
              }
              const best = visible.find(matchBest) || visible[Math.floor(visible.length / 2)];
              shades = visible.map((shade) => ({
                name: shade.shadeName || '',
                hex: shade.hex,
                imageUrl: shade.imageUrl,
                isBest: shade === best,
              }));
            } else if (_hasShade) {
              shades = [primaryShadeRecord, alternativeShadeRecord]
                .filter((record) => record && record.displayValue !== 'N/A')
                .map((record, index) => ({
                  name: record.swatch?.shadeName || record.displayValue,
                  hex: record.swatch?.hex,
                  imageUrl: record.swatch?.imageUrl,
                  isBest: index === 0,
                }));
            }
            const bestShade = shades.find((shade) => shade.isBest);

            return (
              <div key={blockIndex} className={productCardClass}>
                <ShadeMatchCard productName={productName} brand={brand} heroUrl={heroUrl} shades={shades} />

                {/* Alternative shade — compact inline chip under the header. Hidden when the trio already shows neighbours. */}
                {!showTrio && !_hasShade && alternativeShadeRecord && alternativeShadeRecord.displayValue !== 'N/A' && (
                  <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                    <span className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#2A324B]/45">Or</span>
                    {(alternativeShadeRecord.swatch?.imageUrl || alternativeShadeRecord.swatch?.hex) && (
                      <ShadeSwatch
                        imageUrl={alternativeShadeRecord.swatch.imageUrl}
                        hex={alternativeShadeRecord.swatch.hex}
                        size={18}
                        alt={alternativeShadeRecord.swatch.shadeName ? `${alternativeShadeRecord.swatch.shadeName} swatch` : 'shade swatch'}
                      />
                    )}
                    <span className="text-[13px] text-[#2A324B]/70 break-words min-w-0">
                      {alternativeShadeRecord.shadeHead}{alternativeShadeRecord.shadeTail ? ` · ${alternativeShadeRecord.shadeTail}` : ''}
                    </span>
                  </div>
                )}

                {!_hasShade && otherBodyLines.length > 0 && (
                <div className="mt-3 space-y-1.5 text-sm text-[#2A324B]/85">
                  {otherBodyLines.map((line, lineIndex) => {
                    const match = line.match(detailLabelRegex);
                    if (match) {
                      const isAlternativeShade = /^Alternative shade$/i.test(match[1]);
                      const isShadeLabel = /^(Recommended shade|Alternative shade)$/i.test(match[1]);
                      // Determine what to render for the shade value.
                      // BULLETPROOF L: the catalog stores verbose shade_names like
                      // "Atelier XV – deep espresso, neutral undertone" (45 chars + commas).
                      // The previous heuristic ("treat as ingredient list and N/A out")
                      // false-positived on every real catalog shade. New rules:
                      //   1. If we can find this shade in shadeMatches (server-attached
                      //      catalog payload) — accept it verbatim regardless of length.
                      //   2. Else, only N/A out when the value is clearly an ingredient
                      //      list: 3+ commas AND >60 chars.
                      let shadeValue = match[2].trim();
                      let swatch = null;
                      if (isShadeLabel && shadeValue && shadeValue !== 'N/A') {
                        swatch = swatchIndex.lookup(productName, shadeValue);
                        if (!swatch) {
                          const head = shadeValue.split(/\s+[—–-]\s+/)[0]?.trim();
                          if (head && head !== shadeValue) {
                            swatch = swatchIndex.lookup(productName, head);
                          }
                        }
                      }
                      if (isShadeLabel && shadeValue !== 'N/A' && !swatch) {
                        // Only apply the ingredient-list guard when we have NO catalog
                        // confirmation. Tightened thresholds so a single descriptor comma
                        // and verbose shade name no longer clobber a real shade.
                        const commaCount = (shadeValue.match(/,/g) || []).length;
                        if (commaCount >= 3 && shadeValue.length > 60) {
                          shadeValue = 'N/A';
                        }
                      }
                      // For display: if we matched a swatch, prefer showing the catalog's
                      // canonical shade_name (cleaner formatting). Otherwise show what the LLM wrote.
                      const displayValue = isShadeLabel
                        ? (swatch?.shadeName || shadeValue)
                        : match[2].trim();
                      return (
                        <p key={lineIndex} className={`leading-relaxed flex items-center flex-wrap gap-x-1.5 gap-y-0.5 ${isAlternativeShade ? 'text-[#2A324B]/80' : ''}`}>
                          <span className={`text-xs font-bold uppercase tracking-wide ${isAlternativeShade ? 'text-[#2A324B]/70' : 'text-[#2A324B]'}`}>{match[1]}</span>
                          {swatch && (
                            <ShadeSwatch
                              imageUrl={swatch.imageUrl}
                              hex={swatch.hex}
                              size={isAlternativeShade ? 22 : 26}
                              alt={swatch.shadeName ? `${swatch.shadeName} swatch` : 'shade swatch'}
                            />
                          )}
                          <span>{formatInlineText(displayValue)}</span>
                        </p>
                      );
                    }
                    return <p key={lineIndex} className="leading-relaxed">{formatInlineText(line)}</p>;
                  })}
                </div>
                )}
                {onSaveProduct && userId && (
                  <button
                    type="button"
                    onClick={() => {
                      setPendingSave({ data: { name: productName, brand, category: _hasShade ? 'makeup' : 'skincare', image: heroUrl || primaryShadeRecord?.swatch?.imageUrl || '', description: block, recommended_shade: _hasShade ? (bestShade?.name || primaryShadeRecord.displayValue) : null, swatch_image_url: bestShade?.imageUrl || '', swatch_hex: bestShade?.hex || null, shade_options: shades }, productKey });
                    }}
                    disabled={isSaved}
                    className="mt-4 pt-3 w-full border-t border-[#2A324B]/10 flex items-center justify-center gap-2 text-[13px] font-semibold text-[#2A324B] hover:text-[#2A1212] disabled:opacity-70"
                  >
                    <Heart className="w-[17px] h-[17px]" aria-hidden="true" fill={isSaved ? '#2A324B' : 'none'} />
                    {isSaved ? 'Saved' : 'Save product'}
                  </button>
                )}
              </div>
            );
          })}
          <div className="mt-2 pt-3 border-t border-[#2A324B]/8 flex items-start gap-1.5 text-[13px] text-[#2A324B]/50 leading-snug">
            <Check className="h-3.5 w-3.5 text-green-600 flex-shrink-0 mt-0.5" />
            <span>Chosen for your skin only. We earn no commission from these.</span>
          </div>
        </div>
      );
    }

    // Fallback: split by bullets, numbers, or single lines
    let items = [];
    if (text.includes('.,•') || text.includes('. •')) {
      items = text.split(/\.,•|\.•|\.\s+•/).map(s => s.trim()).filter(s => s);
    } else if (text.includes('•')) {
      items = text.split('•').map(s => s.trim()).filter(s => s);
    } else {
      const numberedMatch = text.match(/^\d+\)/m);
      if (numberedMatch) {
        items = text.split(/(?=^\d+\))/m).map(s => s.trim()).filter(s => s);
      } else {
        items = text.split('\n').map(s => s.trim()).filter(s => s);
      }
    }
    if (items.length === 0) items = [text];

    return (
      <div className="space-y-0">
        {items.map((item, index) => {
          if (!item) return null;
          const cleanItem = item.replace(/^[•\-\*,.\s]+/, '').trim();
          if (!cleanItem) return null;
          const isDetailLine = /^(Recommended shade|Alternative shade|Why it works|Key ingredients|Texture\/finish|How to use|Price range|When to use|Best for|Application|Notes?):/i.test(cleanItem);
          let displayContent;
          let productName = null;
          let brand = null;
          let description = null;
          const strippedItem = cleanItem.replace(/\*\*/g, '');
          const productByBrandMatch = strippedItem.match(/^(.+?)\s+by\s+(.+?)\s*[-–—]\s*(.+)/);
          const productWithDashMatch = strippedItem.match(/^(.+?)\s*[-–—]\s*(.+)/);
          const productNameOnlyMatch = strippedItem.match(/^(.+?)$/);

          if (productByBrandMatch) {
            productName = productByBrandMatch[1].trim();
            brand = productByBrandMatch[2].trim();
            description = productByBrandMatch[3].trim();
            displayContent = (<> <strong className="font-semibold text-[#2A324B]">{productName}</strong> by {brand} - {description} </>);
          } else if (productWithDashMatch) {
            productName = productWithDashMatch[1].trim();
            description = productWithDashMatch[2].trim();
            displayContent = (<> <strong className="font-semibold text-[#2A324B]">{productName}</strong> - {formatInlineText(description)} </>);
          } else if (productNameOnlyMatch && strippedItem.length < 100) {
            productName = productNameOnlyMatch[1].trim();
            displayContent = <strong className="font-semibold text-[#2A324B]">{productName}</strong>;
          } else {
            const boldMatch = cleanItem.match(/\*\*([^*]+)\*\*/);
            if (boldMatch) {
              productName = boldMatch[1].trim();
              const rest = cleanItem.replace(/\*\*[^*]+\*\*/, '').trim();
              displayContent = (<> <strong className="font-semibold text-[#2A324B]">{productName}</strong> {rest && <> - {formatInlineText(rest)}</>} </>);
            } else {
              const firstPhraseMatch = cleanItem.match(/^([^,.\-–—]+?)(?:\s*[-–—,.]\s*|$)/);
              if (firstPhraseMatch && firstPhraseMatch[1].trim().length > 3 && firstPhraseMatch[1].trim().length < 60) {
                productName = firstPhraseMatch[1].trim();
                const rest = cleanItem.substring(firstPhraseMatch[0].length).trim();
                displayContent = (<> <strong className="font-semibold text-[#2A324B]">{productName}</strong> {rest && <> - {formatInlineText(rest)}</>} </>);
              } else {
                displayContent = formatInlineText(cleanItem);
              }
            }
          }
          const productInfo = productName ? { name: productName, brand: brand || '', description: description || strippedItem } : null;
          const pk = productInfo ? `${productInfo.name}-${productInfo.brand}` : null;
          const saved = pk ? savedProducts.has(pk) : false;

          return (
            <div key={index} className={productCardClass}>
              <div className="text-sm leading-relaxed text-[#2A324B]/90">{displayContent}</div>
              {!isDetailLine && onSaveProduct && userId && productInfo?.name && (
                <button
                  onClick={() => {
                    setPendingSave({ data: { name: productInfo.name, brand: productInfo.brand, category: 'skincare', description: productInfo.description }, productKey: pk });
                  }}
                  className={`mt-3 w-full flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border border-[#2A324B]/15 text-sm font-medium ${saved ? 'bg-[#F3D5B5]/30 text-[#2A324B]/80' : 'bg-white hover:bg-[#F2EAE1]/50 text-[#2A324B]'}`}
                  disabled={saved}
                >
                  {saved ? <><Check className="h-4 w-4" /> Saved to catalogue</> : <><Bookmark className="h-4 w-4" /> Save to catalogue</>}
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Special formatting for Routine tab content
  const formatRoutineContent = (text) => {
    if (!text) return null;
    if (typeof text !== 'string') text = String(text);

    const lines = text.split('\n');
    const nodes = [];
    let key = 0;

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        nodes.push(<div key={key++} className="h-1" />);
        return;
      }

      // Markdown headings: ##, ###, #### etc
      const mdHeading = trimmed.match(/^#{1,6}\s+(.+)$/);
      if (mdHeading) {
        nodes.push(
          <p key={key++} className="text-xs font-bold uppercase tracking-wide text-[#C8976E] mt-2 mb-0">
            {mdHeading[1]}
          </p>
        );
        return;
      }

      // Section heading: short text ending with colon, or **bold** standalone, or ALL CAPS short
      const isSectionHead =
        (trimmed.endsWith(')') && trimmed.length < 60 && /^[A-Z(]/.test(trimmed)) ||
        (trimmed.endsWith(':') && trimmed.length < 60) ||
        /^\*\*[^*]+\*\*[:\s]*$/.test(trimmed);
      if (isSectionHead) {
        nodes.push(
          <p key={key++} className="text-xs font-bold uppercase tracking-wide text-[#C8976E] mt-2 mb-0">
            {formatInlineText(trimmed.replace(/[*:]/g, ''))}
          </p>
        );
        return;
      }

      // Numbered step: "1. " or "1) "
      const stepMatch = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
      if (stepMatch) {
        nodes.push(
          <div key={key++} className="flex items-start gap-2 py-0.5">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#2A324B] flex items-center justify-center text-xs font-semibold text-white">
              {stepMatch[1]}
            </span>
            <p className="text-sm leading-snug text-[#2A324B]/90 flex-1 pt-0.5">
              {formatInlineText(stepMatch[2])}
            </p>
          </div>
        );
        return;
      }

      // Bullet line
      const bulletMatch = trimmed.match(/^[-–•*]\s+(.+)$/);
      if (bulletMatch) {
        nodes.push(
          <div key={key++} className="flex items-start gap-2 pl-9">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C8976E]/50 flex-shrink-0 mt-[5px]" />
            <p className="text-sm leading-snug text-[#2A324B]/80 flex-1">
              {formatInlineText(bulletMatch[1])}
            </p>
          </div>
        );
        return;
      }

      // Regular text
      nodes.push(
        <p key={key++} className="text-sm leading-snug text-[#2A324B]/90">
          {formatInlineText(trimmed)}
        </p>
      );
    });

    return <div className="space-y-0.5">{nodes}</div>;
  };

  // A tab is "available" only when its value actually has something to show.
  // The structured reply often has all three keys with two of them null —
  // those nulls should not render as tabs (otherwise every plain-advice
  // follow-up looks like a tabbed report).
  const tabHasContent = (key) => {
    if (!content || typeof content !== 'object') return false;
    if (!Object.prototype.hasOwnProperty.call(content, key)) return false;
    const v = content[key];
    if (v == null) return false;
    if (typeof v === 'string') return v.trim().length > 0;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return Boolean(v);
  };

  const availableTabs = useMemo(
    () => TAB_DEFS.filter((t) => tabHasContent(t.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [content]
  );

  // Stable string so we don't re-run effects when only object identity changes (same tab ids)
  const tabIdsKey = useMemo(
    () => availableTabs.map((t) => t.id).join('|'),
    [availableTabs]
  );

  const [activeTab, setActiveTab] = useState('advice');
  // 2026-06-11 (Sofia C): answer-first advice. The lead (answer) shows; the
  // detailed reasoning collapses under a "Show full reasoning" expander.
  const [adviceExpanded, setAdviceExpanded] = useState(false);
  // 2026-06-12 (Sofia merge): when a reply includes product recommendations we
  // collapse Advice + Products into one answer-first flow. Routine stays a
  // SEPARATE tab (Sofia kept it) — this tracks which of the two is active.
  const [mergedTab, setMergedTab] = useState('advice');

  // Split the advice into a short LEAD (the answer, always visible) and the
  // DETAIL (the reasoning, behind the expander).
  //   - If the backend provides a dedicated `advice_detail` field (future,
  //     cleanest), lead = advice, detail = advice_detail.
  //   - Otherwise split the single advice string: the FIRST paragraph is the
  //     lead, everything after is detail. Works on today's responses so this
  //     is previewable on localhost before any backend change ships.
  const splitAdvice = (adviceText, adviceDetail) => {
    const lead = (adviceText == null ? '' : (Array.isArray(adviceText) ? adviceText.join('\n') : String(adviceText))).trim();
    if (adviceDetail != null) {
      const detail = (Array.isArray(adviceDetail) ? adviceDetail.join('\n') : String(adviceDetail)).trim();
      return { lead, detail };
    }
    if (!lead) return { lead: '', detail: '' };
    // Split on the first blank line (paragraph break). First para = answer.
    const parts = lead.split(/\n\s*\n/);
    if (parts.length <= 1) {
      // No paragraph break — keep it all as the lead, nothing to expand.
      return { lead, detail: '' };
    }
    return { lead: parts[0].trim(), detail: parts.slice(1).join('\n\n').trim() };
  };

  // 2026-06-12 (Sofia): the follow-up question ("Do you want X or Y?") used to be
  // a transient client-side bubble that vanished on reload. Render it INLINE at the
  // bottom of the message instead, from content.follow_up, so it persists and always
  // shows in the merged view. The separate bubble in ChatComponent is removed to
  // avoid duplication.
  const followUpText =
    content && typeof content === 'object' && typeof content.follow_up === 'string'
      ? content.follow_up.trim()
      : '';
  const renderFollowUp = () =>
    followUpText ? (
      <div className="mt-4 pl-3.5 border-l-2 border-[#E3B587]">
        <p className="text-[15px] italic text-[#2A324B]/70 leading-relaxed">{formatInlineText(followUpText)}</p>
      </div>
    ) : null;

  // 2026-06-29 (quiz/clarify): the model can ask a small either/or via `clarify`
  // ({question, options}). Choices sit on the user's side; tapping sends the
  // option as the next message and replaces the choices with that user bubble.
  const clarify = content && typeof content === 'object' ? content.clarify : null;
  const clarifyQuestion = clarify && typeof clarify === 'object' ? String(clarify.question || '').trim() : '';
  const clarifyOptions = clarify && Array.isArray(clarify.options)
    ? clarify.options.filter((o) => typeof o === 'string' && o.trim()).map((o) => o.trim()).slice(0, 4)
    : [];
  const clarifyImages = clarify && typeof clarify.product_images === 'object' && clarify.product_images
    ? clarify.product_images : {};
  const clarifyImageFor = (option) => publicCatalogImageUrl(clarifyImages[option], 'heroes')
    || publicCatalogImageUrl(clarifyImages[option], 'swatches');
  const hasClarifyImages = clarifyOptions.some((option) => !!clarifyImageFor(option));
  const showClarifyChoices = !clarifySubmitted && !clarifyAnswered;
  const sendClarifyChoice = (option) => {
    if (!onAsk || clarifySubmitted || selectedClarifyOption !== null) return;
    setSelectedClarifyOption(option);
    // Let the selection settle visually before the normal outgoing bubble
    // replaces the choices. The border width and icon slot stay fixed.
    clarifyTimer.current = setTimeout(() => {
      setClarifySubmitted(true);
      onAsk(option);
      clarifyTimer.current = null;
    }, 220);
  };
  const renderClarify = () =>
    (clarifyQuestion || clarifyOptions.length > 0) ? (
      <div className="mt-4">
        {clarifyQuestion && (
          <p className="text-[15px] font-medium text-[#2A324B] leading-snug mb-2.5">{clarifyQuestion}</p>
        )}
        {showClarifyChoices && clarifyOptions.length > 0 && (
          <div className="flex flex-col gap-2">
            {clarifyOptions.map((opt, i) => {
              const selected = selectedClarifyOption === opt;
              const display = clarifyOptionDisplay(opt, clarifyQuestion, clarifyOptions);
              const productImage = clarifyImageFor(opt);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => sendClarifyChoice(opt)}
                  disabled={!onAsk || selectedClarifyOption !== null}
                  aria-label={`Send ${opt}`}
                  aria-pressed={selected}
                  className={`w-full flex items-center gap-3 px-3 py-3 rounded-2xl border-2 text-left text-[#2A324B] transition-transform transition-colors transition-shadow duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2A324B]/25 active:scale-[0.99] ${selected ? 'border-[#2A324B] bg-[#F2F3F6] shadow-md' : 'border-[#2A324B]/15 bg-white hover:border-[#2A324B]/55 hover:shadow-sm'}`}
                >
                  {productImage ? (
                    <span className="w-12 h-12 rounded-xl flex-shrink-0 overflow-hidden bg-[#F2EAE1]">
                      <img src={productImage} alt="" aria-hidden="true" className="w-full h-full object-cover" onError={(event) => { event.currentTarget.style.display = 'none'; }} />
                    </span>
                  ) : (
                    <span className={`${hasClarifyImages ? 'w-12 h-12' : 'w-8 h-8'} rounded-full flex-shrink-0 flex items-center justify-center text-[13px] font-semibold ${selected ? 'bg-[#2A324B] text-white' : 'bg-[#F2EAE1] text-[#2A324B]/70'}`}>{i + 1}</span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-semibold leading-snug break-words">{display.title}</span>
                    {display.detail && <span className="block mt-0.5 text-[12px] font-normal leading-snug text-[#2A324B]/60 break-words">{display.detail}</span>}
                  </span>
                  <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center" aria-hidden="true">{selected && <Check className="w-5 h-5" />}</span>
                </button>
              );
            })}
          </div>
        )}
        {/* The user can always TYPE instead of tapping — the chat input sits right
            below. Make that explicit (esp. when their answer isn't an option). */}
        {showClarifyChoices && <p className="mt-2.5 text-[13px] text-[#2A324B]/55">…or just type your answer below.</p>}
      </div>
    ) : null;

  // 2026-08-26 (Sofia): the section CTA is the SAME button we already shipped, just
  // moved to the TOP of its tab so it's the first thing there and the model's advice
  // flows underneath. Placement comes from the cta itself: routine CTA -> Routine
  // tab (camera glyph, "Scan / Open My Routine"), product CTA -> Advice tab (arrow,
  // "See full formula analysis").
  const ctaPlacement = sectionCta
    ? (sectionCta.placement || (sectionCta.label === 'See full formula analysis' ? 'advice' : 'routine'))
    : null;
  const renderSectionCta = () =>
    sectionCta ? (
      <button
        type="button"
        onClick={sectionCta.onClick}
        className="w-full flex items-center justify-center gap-2 rounded-full bg-[#2A1212] hover:bg-[#3A1E1E] active:scale-[0.99] px-5 py-3.5 shadow-sm transition-colors"
      >
        {ctaPlacement === 'advice' ? (
          <svg className="w-[18px] h-[18px] flex-shrink-0 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        ) : (
          <svg className="w-[18px] h-[18px] flex-shrink-0 text-white" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
          </svg>
        )}
        <span className="text-sm font-semibold text-white">{sectionCta.label}</span>
      </button>
    ) : null;

  // When the set of tabs for this payload changes, or activeTab is invalid (e.g. React reused row key), fix selection
  useEffect(() => {
    const ids = tabIdsKey ? tabIdsKey.split('|') : [];
    setActiveTab((prev) => {
      if (ids.includes(prev)) return prev;
      return ids[0] || 'advice';
    });
  }, [tabIdsKey]);

  // Only show the tab bar when there's more than one section with content.
  // Plain advice replies (most follow-ups) skip the tabs entirely and render
  // the single section's content directly.
  const showTabs = availableTabs.length > 1;

  // 2026-06-12 (Sofia): MERGED ANSWER-FIRST VIEW.
  // When the reply recommends products, the old Advice/Products tab split was
  // redundant (the product card already carries why-it-works / texture / how-to-
  // apply) and pushed too much text. Instead, render ONE flow:
  //   1. lead answer (1–2 sentences, always visible)
  //   2. the product card(s) — swatch for makeup, product photo for skincare
  //   3. "Show full reasoning" — the deep, personal insight, collapsed
  //   4. routine (if any) under its own expander
  // Pure-advice / routine-only replies keep the existing rendering below.
  // The catalogue result and the model-authored product prose are separate
  // response fields. A valid shade search must still render cards when the
  // model omits `content.products`; otherwise verified matches silently vanish.
  const verifiedShadeProductContent = (() => {
    if (!Array.isArray(shadeMatches) || shadeMatches.length === 0) return null;
    const seen = new Set();
    const blocks = [];
    shadeMatches.forEach((match) => {
      const title = String(match?.product_title || '').trim();
      if (!title) return;
      const key = title.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      const shade = String(match?.shade_name || '').trim();
      blocks.push(`**${title}**${shade ? `\nRecommended shade: ${shade}` : ''}`);
    });
    return blocks.length ? blocks.join('\n\n') : null;
  })();
  const productContent = tabHasContent('products') ? content.products : verifiedShadeProductContent;
  const mergedMode = !!productContent;
  if (mergedMode) {
    const { lead, detail } = splitAdvice(content.advice, content.advice_detail);
    const hasRoutine = tabHasContent('routine');
    const mTabs = [{ id: 'advice', label: 'Advice' }];
    if (hasRoutine) mTabs.push({ id: 'routine', label: 'Routine' });
    const active = (hasRoutine && mergedTab === 'routine') ? 'routine' : 'advice';
    return (
      <div className="w-full">
        {/* Tab bar — only when there's a routine to switch to. Advice carries the
            merged answer-first content (answer → cards → reasoning). */}
        {hasRoutine && (
          <div className="flex items-center gap-5 mb-3 border-b border-[#341C1C]/10">
            {mTabs.map((t) => {
              const isActive = active === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setMergedTab(t.id)}
                  className={`relative text-xs pb-2 pt-1 font-medium tracking-wide transition-colors ${
                    isActive ? 'text-[#2A1212]' : 'text-[#2A324B]/45 hover:text-[#2A324B]/70'
                  }`}
                >
                  {t.label}
                  {isActive && <span className="absolute left-0 right-0 -bottom-px h-px bg-[#2A1212]" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        )}

        {active === 'routine' ? (
          <div>
            {ctaPlacement === 'routine' && <div className="mb-3">{renderSectionCta()}</div>}
            {formatRoutineContent(content.routine)}
          </div>
        ) : (
          <div className="space-y-4">
            {/* CTA at the top of the Advice tab (product analysis turns) */}
            {ctaPlacement === 'advice' && renderSectionCta()}
            {/* 1. Lead answer */}
            {lead && <div className="space-y-2">{formatText(lead)}</div>}

            {/* 2. Deep reasoning — collapsed, ABOVE the cards (Sofia 2026-06-12) so
                   the toggle is visible right under the answer; collapsed it's one line. */}
            {detail && (
              <div>
                <button
                  type="button"
                  onClick={() => setAdviceExpanded((v) => !v)}
                  className="w-full flex items-center justify-between py-2.5 text-left"
                  style={{ borderTop: '1px solid rgba(42,50,75,0.10)', borderBottom: '1px solid rgba(42,50,75,0.10)' }}
                >
                  <span className="text-[13px] font-semibold text-[#2A324B]">
                    {adviceExpanded ? 'Hide reasoning' : 'Show full reasoning'}
                  </span>
                  <ChevronDown
                    className="w-4 h-4 flex-shrink-0 text-[#2A324B]/45"
                    style={{ transform: adviceExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                  />
                </button>
                {adviceExpanded && <div className="pt-3">{formatText(detail, true)}</div>}
              </div>
            )}

            {/* 3. Product card(s) */}
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-xs text-[#2A324B]/60 font-medium">Recommended for you</span>
                {brandTrigger}
              </div>
              {formatProductContent(productContent)}
            </div>

            {/* 4. Follow-up question — keeps the conversation going */}
            {renderFollowUp()}

            {/* 5. Clarify — tappable either/or the model wants answered */}
            {renderClarify()}
          </div>
        )}
        {/* Save-to picker — MUST be in this (answer-first) return too; the chat
            uses this path, not the main return below. (Fixes hearts not saving.) */}
        {pendingSave && (
          <SavePicker
            product={pendingSave.data}
            onClose={() => setPendingSave(null)}
            onSave={confirmSave}
          />
        )}
        <IngredientGlossarySheet entryId={glossaryEntry} onClose={() => setGlossaryEntry(null)} />
        {brandSheet}
      </div>
    );
  }

  return (
    <div className="w-full">
      {showTabs && (
        <div className="flex items-center gap-5 mb-3 border-b border-[#341C1C]/10">
          {availableTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`relative text-xs pb-2 pt-1 font-medium tracking-wide transition-colors ${
                  isActive
                    ? 'text-[#2A1212]'
                    : 'text-[#2A324B]/45 hover:text-[#2A324B]/70'
                }`}
              >
                {tab.label}
                {isActive && (
                  <span
                    className="absolute left-0 right-0 -bottom-px h-px bg-[#2A1212]"
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Tab Content */}
      <div className="space-y-2">
        {availableTabs.map((tab) => (
          <div
            key={tab.id}
            className={activeTab === tab.id ? 'block' : 'hidden'}
          >
            {tab.id === 'products' &&
              content[tab.id] != null &&
              String(content[tab.id]).trim() !== '' && (
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="text-xs text-[#2A324B]/60 font-medium">Recommended for you</span>
                  {brandTrigger}
                </div>
              )}
            {tab.id === 'products' ? (
              content[tab.id] != null && String(content[tab.id]).trim() !== '' ? (
                formatProductContent(content[tab.id])
              ) : (
                <p className="text-sm text-[#2A324B]/60 leading-snug">{EMPTY_SECTION_COPY.products}</p>
              )
            ) : tab.id === 'routine' ? (
              <>
                {ctaPlacement === 'routine' && <div className="mb-3">{renderSectionCta()}</div>}
                {content[tab.id] != null &&
                (typeof content[tab.id] === 'string' ? content[tab.id].trim() !== '' :
                 Array.isArray(content[tab.id]) ? content[tab.id].length > 0 : false) ? (
                  formatRoutineContent(content[tab.id])
                ) : (
                  <p className="text-sm text-[#2A324B]/60 leading-snug">{EMPTY_SECTION_COPY.routine}</p>
                )}
              </>
            ) : tab.id === 'advice' ? (
              // 2026-06-11 (Sofia C): answer-first. Lead (the answer) always
              // shows; reasoning collapses under "Show full reasoning".
              (() => {
                // The welcome / intro renders in FULL — it isn't an answer+reasoning
                // reply, so don't bury most of it under "Show full reasoning".
                if (noReasoningSplit) {
                  return <div className="space-y-2">{formatText(content.advice)}</div>;
                }
                const { lead, detail } = splitAdvice(content.advice, content.advice_detail);
                return (
                  <div className="space-y-2">
                    {/* CTA at the top of the Advice tab (product analysis turns) */}
                    {ctaPlacement === 'advice' && <div className="mb-1">{renderSectionCta()}</div>}
                    {formatText(lead)}
                    {detail && (
                      <div className="pt-1">
                        <button
                          type="button"
                          onClick={() => setAdviceExpanded((v) => !v)}
                          className="w-full flex items-center justify-between py-2.5 text-left"
                          style={{ borderTop: '1px solid rgba(42,50,75,0.10)' }}
                        >
                          <span className="text-[13px] font-semibold text-[#2A324B]">
                            {adviceExpanded ? 'Hide reasoning' : 'Show full reasoning'}
                          </span>
                          <ChevronDown
                            className="w-4 h-4 flex-shrink-0 text-[#2A324B]/45"
                            style={{ transform: adviceExpanded ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
                          />
                        </button>
                        {adviceExpanded && <div className="pt-3">{formatText(detail, true)}</div>}
                      </div>
                    )}
                  </div>
                );
              })()
            ) : (
              formatText(content[tab.id])
            )}
          </div>
        ))}
      </div>
      {/* Follow-up question — keeps the conversation going (inline so it persists) */}
      {renderFollowUp()}
      {renderClarify()}
      {pendingSave && (
        <SavePicker
          product={pendingSave.data}
          onClose={() => setPendingSave(null)}
          onSave={confirmSave}
        />
      )}
      <IngredientGlossarySheet entryId={glossaryEntry} onClose={() => setGlossaryEntry(null)} />
      {brandSheet}
    </div>
  );
};

export default StructuredMessage;
